<template>
    <wrapper-features-templates-operations
    showFooter
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :footerButtonsParams="buttonsFooterData"
    :scrollValue="scrollValue"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="navigateByName(routeList.USER_AREA_DEFINITIONS_NAME)">
        <div class="content-wrapper" v-if="!showLoader && !applyErrorState">
            <div class="text-info" v-html="textInfoDisplayer"/>
            <input-generic
            id="friendlyUserName"
            :params="friendlyUserNameInput"
            :inputValue="friendlyUserNameData"
            @onBlur="friendlyUserNameInputValidation"
            @input="[friendlyUserNameData = $event, clearFieldError()]"/>
        </div>
    </wrapper-features-templates-operations>
    <!-- Error Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>

/* Generic */
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import InputGeneric from '@/components/generic/inputs/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref, computed } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { removeAccents, onlyLettersNoSpace } from '@/assets/scripts/validators'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { changeUserFriendlyName } = mapActions(moduleMapper.USER_AREA_DEFINITIONS_FRIENDLY_USERNAME)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const scrollValue = ref(null)

/*************************************************
*                                                *
*                      Data                      *
*                                                *
*************************************************/
const friendlyUserNameData = ref('')

const textInfoDisplayer = computed(() => {
    const text1 = t('userArea.definitions.friendlyUsername.textInfo.hasFriendlyUserName')
    const text2 = t('userArea.definitions.friendlyUsername.textInfo.noFriendlyUserName')

    return getAllCustomerInfo.value.hasFriendlyUsername ? text1 : text2
})

/* Friendly User Name Input */
const friendlyUserNameInput = ref({
    labelText: t('userArea.definitions.friendlyUsername.friendlyUserNameInput.labelText'),
    bottomMessage: t('userArea.definitions.friendlyUsername.friendlyUserNameInput.bottomMessage'),
    placeholder: t('userArea.definitions.friendlyUsername.friendlyUserNameInput.placeholder'),
    maxLength: 15,
    errorState: false,
    errorMessage: '',
    ourMask: {
        parseInputText: value => { return removeAccents(value) },
        validatorFn: value => { return onlyLettersNoSpace(value) }
    }
})

const friendlyUserNameInputValidation = () => {
    if (friendlyUserNameData.value.length < 6 && friendlyUserNameData.value !== '') {
        friendlyUserNameInput.value.errorState = true
        friendlyUserNameInput.value.errorMessage = t('userArea.definitions.friendlyUsername.friendlyUserNameInput.errorMessage')
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
const clearFieldError = () => {
    friendlyUserNameInput.value.errorMessage = ''
    friendlyUserNameInput.value.errorState = false
}

/*************************************************
*                                                *
*        Change UserFriendlyName Request         *
*                                                *
*************************************************/
const changeUserFriendlyNameRequest = async () => {
    showOverlayLoader.value = true

    const payload = {
        friendlyUsername: friendlyUserNameData.value
    }

    try {
        const res = await changeUserFriendlyName(payload)
        successHandler(res.data)
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, changeUserFriendlyNameRequest)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
    showOverlayLoader.value = false
	feedbackToastData.value = {
		toastType: data.errorLevel.toLowerCase(),
		text: data.message,
		buttons: []
	}
	showFeedbackToast.value = true

    clearFieldError()
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false
    if (data.code === 'friendly_username_already_in_use') {
        friendlyUserNameInput.value.errorState = true
        friendlyUserNameInput.value.errorMessage = t('userArea.definitions.friendlyUsername.friendlyUserNameInput.validateErrorMessage')
    } else {
        feedbackToastData.value = errorObjectConstructor(data, retryFn)

        showFeedbackToast.value = true
    }
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const buttonsFooterData = computed(() => {
    return [{
        label: t('userArea.definitions.friendlyUsername.buttonsFooterData.clear'),
        buttonPosition: 'left',
        callbackFunction: () => { clearFields() }
    },
    {
        label: t('userArea.definitions.friendlyUsername.buttonsFooterData.validate'),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        callbackFunction: () => { validateInput() }
    }]
})

const validateInput = () => {
    const friendlyUserNameShort = friendlyUserNameData.value.length

    if (!friendlyUserNameShort) {
        friendlyUserNameInput.value.errorState = true
        friendlyUserNameInput.value.errorMessage = t('mandatoryFieldError')
    }
    if (friendlyUserNameShort < 6) {
        friendlyUserNameInputValidation()
        return
    }
    changeUserFriendlyNameRequest()
}

const clearFields = () => {
    friendlyUserNameData.value = ''
    friendlyUserNameInput.value.errorState = false
    friendlyUserNameInput.value.errorMessage = null
}
</script>

<style lang="scss" scoped>
.content-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;

    .text-info {
        margin-top: 24px;

        @include H5-Regular;
        font-weight: 400;
        color: var(--secondary-dark-3);

        :deep(span) {
            font-weight: 500;
        }
    }
}
</style>
